import { GameStorageManager, Inject, Singleton } from '@denishussar/html5-game-engine';

export interface ISavePictureData {
    imageId: string;
    timestamp: number;
    src: string; // base64 image
    indexes: number[];
    statistics?: {
        stitchCount: number,
        dragCount: number,
        needleCount: number,
        yarnCount: number,
        zoomInCount: number,
        zoomOutCount: number,
        storeCount: number,
        timeSpent: number,
        rewardsCount: number;
    }
}

export interface ISaveGameData {
    userId: string;
    coins: number;
    addsCooldownTimestamp: number;
    addsDay: number;
    boosterLineCount: number;
    boosterBrushCount: number;
    boosterBucketCount: number;
    tutorialStep: number;
}

@Singleton
export class SavesManager {
    private _saves: ISavePictureData[] = [];
    private _saveGameData: ISaveGameData = {
        userId: null,
        coins: -1,
        addsCooldownTimestamp: 0,
        addsDay: 0,
        boosterLineCount: -1,
        boosterBrushCount: -1,
        boosterBucketCount: -1,
        tutorialStep: 0
    };

    constructor(
        @Inject private _storageManager: GameStorageManager
    ) {
        const saves = this.loadFromLocal();
        if (saves) {
            this._saves = saves;
        }
        const saveGameData = this.loadFromLocalGameData();

        if (saveGameData) {
            // Чтобы не перезаписывать всю таблицу сохраненных данных, чтобы оставались дефолтные для старых игроков
            for (let key in saveGameData) {
                this._saveGameData[key] = saveGameData[key];
            }
        }
    }

    public getSaveGameData(): ISaveGameData {
        return this._saveGameData;
    }

    /*
    public saveAnalyticFlag(key: AnalyticsEventEnum): void {
        if (!AnalyticFlags[key]) {
            console.error("Попытка сохранить несуществующий флаг!");
            return;
        }

        this._saveGameData.analyticFlags /= AnalyticFlags[key];
        this.saveToLocal();
    }

    public hasAnatalyticFlag(key: AnalyticsEventEnum): boolean {
        return !!(this._saveGameData.analyticFlags & AnalyticFlags[key]);
    }
     */

    public saveTutorialStep(value: number): void {
        this._saveGameData.tutorialStep = value;
        this.saveToLocalGameData();
    }

    public saveUserId(value: string): void {
        this._saveGameData.userId = value;
        this.saveToLocalGameData();
    }

    public saveAddsCooldownTimestamp(timestamp: number, day: number): void {
        this._saveGameData.addsCooldownTimestamp = timestamp;
        this._saveGameData.addsDay = day;
        this.saveToLocalGameData();
    }

    public saveCoins(value: number): void {
        this._saveGameData.coins = value;
        this.saveToLocalGameData();
    }

    public saveBoosterLineCount(value: number): void {
        this._saveGameData.boosterLineCount = value;
        this.saveToLocalGameData();
    }

    public saveBoosterBrushCount(value: number): void {
        this._saveGameData.boosterBrushCount = value;
        this.saveToLocalGameData();
    }

    public saveBoosterBucketCount(value: number): void {
        this._saveGameData.boosterBucketCount = value;
        this.saveToLocalGameData();
    }

    public getSavePicture(imageId: string): ISavePictureData {
        return this._saves.filter((saveData: ISavePictureData) => saveData.imageId === imageId)[0];
    }

    public savePicture(imageId: string, src: string | undefined, indexes: number[] | undefined, statistics: ISavePictureData['statistics']) {
        const save = this.getSavePicture(imageId);
        if (!save) {
            this._saves.push({imageId, timestamp: new Date().getTime(), src, indexes, statistics});
        } else {
            save.timestamp = new Date().getTime();
            if (src) {
                save.src = src;
            } else {
                delete save.src;
            }
            if (indexes) {
                save.indexes = indexes;
            } else {
                delete save.indexes;
            }
            if (statistics) {
                save.statistics = statistics;
            } else {
                delete save.statistics;
            }
        }
        this.saveToLocal();
    }

    public saveCustomData(key: string, value: string) {
        this._storageManager.saveData(key, value);
    }

    public getCustomData(key: string): string | undefined {
        return this._storageManager.loadData(key);
    }

    private saveToLocal() {
        this._storageManager.saveData('saves', JSON.stringify(this._saves));
    }

    private loadFromLocal(): ISavePictureData[] {
        return  JSON.parse(this._storageManager.loadData('saves'));
    }

    private loadFromLocalGameData(): ISaveGameData {
        return JSON.parse(this._storageManager.loadData('gameData'));
    }

    private saveToLocalGameData(): void {
        this._storageManager.saveData('gameData', JSON.stringify(this._saveGameData));
    }
}
